import React, { FC, useEffect, useMemo, useState, SyntheticEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { GridRowId, GridToolbar, GridDensity, GridFeatureMode } from '@mui/x-data-grid';
import { Container, CircularProgress, Grid, Slider } from '@mui/material';
import SignalCellular4BarIcon from '@mui/icons-material/SignalCellular4Bar';
import { useTranslation } from 'react-i18next';
import * as actions from '../../actions/index';
import Header from '../../components/header';
import DevicesSignalTable from './DevicesSignalTable';
import SignalStatusTable from './SignalStatusTable';
import { GaEventAction } from '../../common/GaEventAction';
import { GaEventCategory } from '../../common/GaEventCategory';
import GoogleAnalyticsService from '../../common/GoogleAnalyticsService';
import { devicesSignalsQueryClient } from '../../clients/ReactQueryClients/ReactQueryClients';
import { AxiosError } from 'axios';
import { DataGridProps, useToast } from '@thingslog/ui-components';
import DeviceSignal from '../../model/API/DeviceSignal/DeviceSignal';
import SignalStatus from '../../model/API/DeviceSignal/SignalStatus';
import { ReduxState } from '../../reducers';
import TitleHeader from '../../components/TitleHeader/TitleHeader';
import PeriodPicker from '../../components/DatePicker/PeriodPicker';
import { GridFilterModel, GridFilterItem } from '@thingslog/ui-components/src/DataGrid';
import { GridColumnVisibilityModel } from '@thingslog/ui-components';
import { CustomerInfoResponse } from '@thingslog/repositories';

const DevicesSignalPageV2: FC<DeviceSignalProps> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();

  const { deviceNumber: selectedDeviceNumber, fromLevel: fromLevelFromUrl } = useParams();

  const [size, setSize] = useState<number>(10);
  const [fromLevel, setFromLevel] = useState<number>(
    fromLevelFromUrl !== undefined ? Number(fromLevelFromUrl) : 0
  );
  const [toLevel, setToLevel] = useState<number>(
    fromLevelFromUrl !== undefined ? Number(fromLevelFromUrl) + 10 : 100
  );
  const [customerInfo, setCustomerInfo] = useState<CustomerInfoResponse>();
  const [deviceSignalReadings, setDeviceSignalReadings] = useState<DeviceSignal[]>([]);
  const [accountSignalReadings, setAccountSignalReadings] = useState<SignalStatus[]>([]);
  const [selectedTableRows, setSelectedTableRows] = useState<GridRowId[]>([]);
  const [devicesColumnVisibilityModel, setDevicesColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});
  const [signalStatusColumnVisibilityModel, setSignalStatusColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  const companyId = useSelector((state: ReduxState) => state.company.id);
  const selectedDevice = useSelector((state: ReduxState) => state.dev.selectedDevice);
  const toDateTz = useSelector((state: ReduxState) => state.period.toDateTz);
  const fromDateTz = useSelector((state: ReduxState) => state.period.fromDateTz);
  const dataGridFilters: GridFilterItem[] = useSelector(
    (state: ReduxState) => state.dev.dataGridFilters
  );

  const { useDevicesSignal, useSignalStatus } = useMemo(() => devicesSignalsQueryClient, []);

  const handleError = (error: AxiosError): void => {
    toast({
      type: 'error',
      message: error.response?.data.message,
      duration: 5000,
    });
  };

  const { isInitialLoading: isDevicesSignalLoading } = useDevicesSignal(
    companyId,
    fromLevel,
    toLevel,
    {
      enabled: !selectedDeviceNumber,
      onSuccess: (data: DeviceSignal[]) => {
        setDeviceSignalReadings(data);
      },
      onError: (error: AxiosError) => {
        handleError(error);
      },
    }
  );

  const { isInitialLoading: isSignalStatusLoading, refetch: refetchSignalStatus } = useSignalStatus(
    selectedDeviceNumber!,
    fromDateTz,
    toDateTz,
    {
      enabled: !!selectedDeviceNumber,
      onSuccess: (data: SignalStatus[]) => {
        setAccountSignalReadings(data);
      },
      onError: (error: AxiosError) => {
        handleError(error);
      },
    }
  );

  useEffect(() => {
    !!selectedDeviceNumber && refetchSignalStatus();
  }, [toDateTz, fromDateTz]);

  useEffect(() => {
    !selectedDeviceNumber && setCustomerInfo(undefined);
  }, [selectedDeviceNumber]);

  const handleSelectionModelChange = (selectedRowIds: GridRowId[]): void => {
    const selectedDeviceRedux = selectedRowIds.length === 1 ? selectedRowIds : [];
    setSelectedTableRows(selectedRowIds);
    dispatch(actions.devSelected(selectedDeviceRedux, 'radio'));
    GoogleAnalyticsService.triggerEvent(
      GaEventCategory.DEVICE_SIGNAL_PAGE,
      GaEventAction.DEVICES_SIGNAL_PAGE_DEVICE_NUMBER_CLICK,
      String(selectedDeviceRedux)
    );
  };

  const handleOnFilterModelChange = (model: GridFilterModel): void => {
    if (model.items.some((item: GridFilterItem) => item.value !== '')) {
      dispatch(actions.filterChanged(model.items));
    } else {
      dispatch(actions.filterChanged([]));
    }
  };

  const commonSignalTableProps: Omit<DataGridProps, 'columns' | 'rows'> = {
    pageSize: size,
    autoHeight: true,
    disableColumnMenu: true,
    hideFooterSelectedRowCount: true,
    rowsPerPageOptions: [10, 25, 50],
    filterMode: 'client' as GridFeatureMode,
    paginationMode: 'client' as GridFeatureMode,
    density: 'compact' as GridDensity,
    components: {
      Toolbar: GridToolbar,
    },
    onPageSizeChange: (pageSize: number): void => {
      setSize(pageSize);
    },
  };

  return (
    <Header>
      <Container>
        <section className="mb-4">
          <TitleHeader
            className="mb-4"
            title={t('device_signal_title')}
            deviceNumber={selectedDeviceNumber && selectedDevice && selectedDevice.number}
            deviceName={selectedDeviceNumber && selectedDevice && selectedDevice.name}
            customerInfo={selectedDeviceNumber && selectedDevice && selectedDevice.customerInfo}
          />
          {!selectedDeviceNumber && (
            <div className="flex justify-end max-md:justify-center">
              <div>
                <SignalCellular4BarIcon />
              </div>
              <div className="w-64 mx-3">
                <Slider
                  min={0}
                  max={100}
                  size="small"
                  valueLabelDisplay="auto"
                  defaultValue={[fromLevel, toLevel]}
                  marks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((mark: number) => {
                    return { value: mark, label: mark };
                  })}
                  onChangeCommitted={(
                    event: Event | SyntheticEvent<Element, Event>,
                    levelRange: number | number[]
                  ): void => {
                    setFromLevel(levelRange[0]), setToLevel(levelRange[1]);
                  }}
                />
              </div>
            </div>
          )}
          {selectedDeviceNumber && (
            <Grid container spacing={3} justifyContent="flex-end" alignItems="center">
              <PeriodPicker alwaysShowDatePickers />
            </Grid>
          )}
        </section>
        {isDevicesSignalLoading || isSignalStatusLoading ? (
          <div className="mt-20">
            <CircularProgress size={80} thickness={5} />
          </div>
        ) : selectedDeviceNumber ? (
          <SignalStatusTable
            headerHeight={70}
            signalStatusReadings={accountSignalReadings}
            disableSelectionOnClick
            columnVisibilityModel={signalStatusColumnVisibilityModel}
            onColumnVisibilityModelChange={(columnModel: GridColumnVisibilityModel): void => {
              setSignalStatusColumnVisibilityModel(columnModel);
            }}
            componentsProps={{
              toolbar: {
                csvOptions: {
                  fileName: `Signal_${selectedDeviceNumber}`,
                },
              },
            }}
            {...commonSignalTableProps}
          />
        ) : (
          <DevicesSignalTable
            headerHeight={120}
            devicesSignalReadings={deviceSignalReadings}
            checkboxSelection
            selectionModel={selectedTableRows}
            onSelectionModelChange={handleSelectionModelChange}
            filterModel={{ items: dataGridFilters }}
            onFilterModelChange={handleOnFilterModelChange}
            columnVisibilityModel={devicesColumnVisibilityModel}
            onColumnVisibilityModelChange={(columnModel: GridColumnVisibilityModel): void => {
              setDevicesColumnVisibilityModel(columnModel);
            }}
            componentsProps={{
              toolbar: {
                csvOptions: {
                  fileName: 'Signal_export',
                },
              },
            }}
            {...commonSignalTableProps}
          />
        )}
      </Container>
    </Header>
  );
};

interface DeviceSignalProps {}

export default DevicesSignalPageV2;
